/**
 * FinanceInvoices — live invoice list from /v1/finance/invoices (list-select;
 * no per-id fetch) with real per-status filter counts, a vendor search, an
 * overdue-first table, and a detail drawer carrying the HITL approve/reject gate
 * (/v1/finance/invoices/{id}/approve|reject — a status flip, NOT a Xero push).
 *
 * GATE-2 restyle (handoff): the list is a flex row grid mirroring HR Tickets;
 * overdue rows carry the attention.* amber treatment (2px left bar + row tint +
 * OVERDUE wordmark) and sort to the top; paid/rejected rows dim. Data honesty
 * (per the STEP-A read-path anchor): the invoice read path exposes NO extraction
 * confidence, NO payment terms, and NO received date — those are OMITTED, never
 * fabricated. Real fields only: amount, currency, due date, line_items
 * (description/quantity/amount), ai_summary, source, notes, approver.
 *
 * All colours/sizes flow from theme/tokens.ts. Invoice status renders as the
 * canonical statusTicket dot/label palette (pending = ochre, approved = sage,
 * rejected = grey, paid = faint) — aliased, so no hex ever lives in this file.
 */
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Check, Upload } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { usePolledAsync, fetchInvoices, approveInvoice, rejectInvoice, ingestInvoicePDF } from '../../api'
import type { FinanceInvoice } from '../../types'
import { LineItemsCard } from './LineItemsCard'
import { money, fxProvenance } from '../../../../lib/money'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { accent, attention, ink, neutral, overlay, statusTicket } from '../../../../theme/tokens'

const STATUSES = ['all', 'pending_review', 'approved', 'rejected', 'paid'] as const

const STATUS_LABEL: Record<string, string> = {
  all: 'All',
  pending_review: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
  paid: 'Paid',
}

// Invoice status → quiet dot + label. Values ARE the canonical statusTicket
// dot/label tokens (the handoff's neutral status palette), aliased so no hex
// literal lives here and the no-raw-hex guard stays green.
const INVOICE_STATUS: Record<string, { dot: string; label: string }> = {
  pending_review: statusTicket.in_progress,
  approved:       statusTicket.resolved,
  rejected:       statusTicket.open,
  paid:           statusTicket.closed,
}

// Post-decision confirmation copy — honest to what each action actually does:
// approve = status flip clearing the invoice for payment; reject = status flip
// that drops it from the pending queue (it does NOT notify the vendor — no
// outbound action exists on this path). No Xero language anywhere.
const CONFIRM: Record<string, { text: string; color: string }> = {
  approved: { text: 'Approved for payment.', color: accent.DEFAULT },
  rejected: { text: 'Rejected — removed from the pending queue.', color: ink.muted },
  paid:     { text: 'Paid.', color: ink.secondary },
}

function fmtDate(d?: string): string {
  return d ? new Date(d).toLocaleDateString('en-GB') : '—'
}

// The k-btn file-picker (S-Tier slice 4). NOT the shared UploadButton, whose
// DashButton chrome stays with its non-Finance consumers — same doctrine as
// FinanceMetric vs MetricCard: markup-level reuse lives with the department.
function UploadInvoiceButton({ onFile }: { onFile: (base64: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const ref = useRef<HTMLInputElement>(null)
  async function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const result = String(reader.result)
          const comma = result.indexOf(',')
          resolve(comma >= 0 ? result.slice(comma + 1) : result)
        }
        reader.onerror = () => reject(reader.error ?? new Error('file read failed'))
        reader.readAsDataURL(file)
      })
      await onFile(b64)
    } finally {
      setBusy(false)
      if (ref.current) ref.current.value = '' // allow re-uploading the same file
    }
  }
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={handleChange} />
      <button className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => ref.current?.click()}>
        <Upload size={14} /> {busy ? 'Uploading…' : 'Upload Invoice'}
      </button>
    </>
  )
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

// Date-only overdue, matching the server's `due_date < CURRENT_DATE` (and Overview).
function daysOverdue(dueDate?: string): number {
  if (!dueDate) return 0
  const due = new Date(dueDate); due.setHours(0, 0, 0, 0)
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const diff = Math.floor((today.getTime() - due.getTime()) / 86_400_000)
  return diff > 0 ? diff : 0
}
function isOverdue(inv: FinanceInvoice): boolean {
  if (inv.status === 'paid' || inv.status === 'rejected') return false
  return daysOverdue(inv.dueDate) > 0
}

// Status = 6px dot + label; unknown statuses fall back to the neutral dot.
function InvoiceStatus({ status }: { status: string }) {
  const cfg = INVOICE_STATUS[status] ?? { dot: neutral.dot, label: ink.secondary }
  return (
    <span className="inline-flex items-center gap-2 text-12 font-medium whitespace-nowrap" style={{ color: cfg.label }}>
      <span className="rounded-full flex-shrink-0" style={{ width: 6, height: 6, background: cfg.dot }} />
      {STATUS_LABEL[status] ?? status.replace(/_/g, ' ')}
    </span>
  )
}

// ── Detail drawer ───────────────────────────────────────────────────────────────

function InvoiceDrawer({ invoice, onClose, onApprove, onReject, busy }: {
  invoice: FinanceInvoice
  onClose: () => void
  onApprove: (id: string) => void
  onReject: (id: string) => void
  busy: boolean
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const items = invoice.lineItems ?? []
  const od = isOverdue(invoice)
  // Approve / reject are records-level finance writes (lib/writeAccess):
  // without access a pending invoice shows no decision footer.
  const { records } = useWriteAccess()
  const canDecide = records && invoice.status === 'pending_review'
  const confirmNote = CONFIRM[invoice.status]

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(760px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header — overline (ref + status), then vendor — amount, then honest source line */}
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="k-eyebrow uppercase">{invoice.invoiceNumber || 'No invoice #'}</span>
              <InvoiceStatus status={invoice.status} />
            </div>
            <div className="text-detail-title font-semibold text-ink-primary truncate">{invoice.vendorName} — {money(invoice.amount ?? 0, invoice.currency)}</div>
            {invoice.source && <div className="text-12 text-ink-muted mt-1">via {invoice.source}</div>}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Overdue banner — real dueDate */}
        {od && (
          <div className="px-6 pt-4 flex-shrink-0">
            <div className="flex items-center gap-2 px-3 py-2 rounded-tile text-12"
              style={{ background: attention['banner-bg'], border: `1px solid ${attention['banner-border']}`, color: attention['banner-text'] }}>
              <span className="rounded-full flex-shrink-0" style={{ width: 6, height: 6, background: attention.indicator }} />
              This invoice is past its due date. Late-payment terms may apply.
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
          {/* Extracted fields — REAL only: Amount + Due date. No confidence bar, no terms. */}
          <div className="rounded-tile bg-surface-panel border border-edge-panel px-5 py-4 grid grid-cols-2 gap-4">
            <div>
              <div className="k-eyebrow uppercase mb-1">Amount</div>
              <div className="text-14 font-semibold text-ink-primary tabular-nums">{money(invoice.amount ?? 0, invoice.currency)}</div>
              {/* FX provenance (SB-6): individual figures only — the captured
                  base and its fixing date; GBP rows render nothing. */}
              {fxProvenance(invoice.baseAmountPence, invoice.fxRateDate) !== '' && (
                <div className="text-11 text-ink-muted mt-0.5">{fxProvenance(invoice.baseAmountPence, invoice.fxRateDate)}</div>
              )}
            </div>
            <div>
              <div className="k-eyebrow uppercase mb-1">Due date</div>
              <div className={`text-14 font-medium tabular-nums ${od ? '' : 'text-ink-primary'}`} style={od ? { color: attention.wordmark } : undefined}>{fmtDate(invoice.dueDate)}</div>
            </div>
          </div>

          {/* AI summary — real (11/11 populated on the read path) */}
          {invoice.aiSummary && (
            <div>
              <div className="k-eyebrow uppercase mb-1">AI extraction summary</div>
              <p className="text-13 text-ink-body leading-relaxed">{invoice.aiSummary}</p>
            </div>
          )}

          {/* Line items — the shared LineItemsCard (one totals renderer for
              this drawer and the Approvals drawer, so they cannot disagree).
              The invoices read path exposes no reconciliation breakdown, so
              this surface always renders the degraded form: the net line sum
              labelled as the line sum — never as the invoice total — above
              the stored gross. */}
          <LineItemsCard items={items} currency={invoice.currency} total={invoice.amount ?? 0} />

          {/* Notes — sparse; show only when present */}
          {invoice.notes && (
            <div>
              <div className="k-eyebrow uppercase mb-1">Notes</div>
              <p className="text-13 text-ink-body">{invoice.notes}</p>
            </div>
          )}

          {/* Approver provenance — real */}
          {invoice.approvedBy && (
            <div className="text-12 text-ink-secondary">
              Approved by <span className="font-medium text-ink-primary">{invoice.approvedBy}</span>
              {invoice.approvedAt && ` on ${new Date(invoice.approvedAt).toLocaleString('en-GB')}`}
            </div>
          )}
        </div>

        {/* Footer — approve/reject while pending_review, else an honest confirmation line */}
        {(canDecide || confirmNote) && (
          <div className="px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
            {canDecide ? (
              <div className="flex items-center gap-2">
                <button onClick={() => onApprove(invoice.id)} disabled={busy}
                  className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
                  <Check size={14} /> {busy ? 'Approving…' : 'Approve for payment'}
                </button>
                <button onClick={() => onReject(invoice.id)} disabled={busy}
                  className="k-btn-secondary flex-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  Reject
                </button>
              </div>
            ) : confirmNote ? (
              <div className="text-13 font-medium" style={{ color: confirmNote.color }}>{confirmNote.text}</div>
            ) : null}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function FinanceInvoices() {
  const { data, loading, error } = usePolledAsync(fetchInvoices, 10_000, [])
  const [invoices, setInvoices] = useState<FinanceInvoice[]>([])
  const [statusFilter, setStatusFilter] = useState<(typeof STATUSES)[number]>('all')
  const [vendor, setVendor] = useState('')
  const [detail, setDetail] = useState<FinanceInvoice | null>(null)
  const [busy, setBusy] = useState(false)
  // Ingesting a PDF is a records-level finance write (lib/writeAccess).
  const { records } = useWriteAccess()

  useEffect(() => { if (data) setInvoices(data) }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  // Dedupe by id (the list is deduped at source — this guards the render).
  const all = Array.from(new Map(invoices.map(i => [i.id, i])).values())
  const total = all.length
  const overdueTotal = all.filter(isOverdue).length
  const countFor = (s: string) => (s === 'all' ? total : all.filter(i => i.status === s).length)

  const hasFilter = statusFilter !== 'all' || vendor.trim() !== ''

  // Filter, then settled-last, overdue-first within active, then soonest due (nulls last).
  const display = all
    .filter(i => {
      if (statusFilter !== 'all' && i.status !== statusFilter) return false
      if (vendor && !i.vendorName.toLowerCase().includes(vendor.toLowerCase())) return false
      return true
    })
    .sort((a, b) => {
      // settled (paid/rejected) sink below every active row
      const sa = (a.status === 'paid' || a.status === 'rejected') ? 1 : 0
      const sb = (b.status === 'paid' || b.status === 'rejected') ? 1 : 0
      if (sa !== sb) return sa - sb
      const ao = isOverdue(a) ? 0 : 1
      const bo = isOverdue(b) ? 0 : 1
      if (ao !== bo) return ao - bo
      const ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity
      const bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity
      return ad - bd
    })

  async function decide(id: string, action: 'approve' | 'reject') {
    setBusy(true)
    try {
      const updated = action === 'approve' ? await approveInvoice(id) : await rejectInvoice(id)
      setInvoices(prev => prev.map(i => (i.id === id ? updated : i)))
      setDetail(d => (d && d.id === id ? updated : d)) // keep the drawer open → footer shows the confirmation line
      toast.success(action === 'approve' ? 'Invoice approved' : 'Invoice rejected')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Action failed'))
    } finally {
      setBusy(false)
    }
  }

  function resetFilters() {
    setStatusFilter('all')
    setVendor('')
  }

  return (
    <div className="page-enter flex flex-col gap-4 mx-auto" style={{ maxWidth: 1040, minWidth: 920 }}>
      <SectionTitle
        title="Invoices"
        subtitle={`Finance · ${total} ${plural(total, 'invoice', 'invoices')} · ${overdueTotal} overdue`}
        action={records && (
          <UploadInvoiceButton
            onFile={async (b64) => {
              try {
                await ingestInvoicePDF(b64)
                toast.success('Invoice uploaded — extraction in progress. It will appear as pending review once processed.')
              } catch (e) {
                toast.error(apiErrorMessage(e, 'Upload failed'))
              }
            }}
          />
        )}
      />

      {/* Status chips (real counts) + vendor search */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATUSES.map(s => {
          const active = statusFilter === s
          return (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 rounded-full text-12 font-medium transition-colors ${active ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
              {STATUS_LABEL[s]} <span className={active ? 'opacity-70' : 'text-ink-muted'}>{countFor(s)}</span>
            </button>
          )
        })}
        <div className="ml-auto">
          <input value={vendor} onChange={e => setVendor(e.target.value)}
            placeholder="Search vendor…"
            className="h-8 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent w-[200px]" />
        </div>
      </div>

      {/* List */}
      <div className="k-card">
        {display.length === 0 ? (
          <EmptyState
            message="Nothing here right now"
            hint={hasFilter ? 'No invoices match this filter.' : 'Uploaded and emailed invoices land here once extracted.'}
            actionLabel={hasFilter ? 'Show all invoices' : undefined}
            onAction={resetFilters}
          />
        ) : (
          <div>
            {/* Column header — 2px transparent left border aligns it with the rows */}
            <div className="finance-thead flex items-center gap-4 px-5 py-2 border-b border-edge-row" style={{ borderLeft: '2px solid transparent' }}>
              <span className="w-32 flex-shrink-0">Invoice</span>
              <span className="flex-1 min-w-0">Vendor</span>
              <span className="w-24 flex-shrink-0 text-right">Amount</span>
              <span className="w-[132px] flex-shrink-0 text-right">Due</span>
              <span className="w-[120px] flex-shrink-0">Status</span>
              <span className="w-12 flex-shrink-0">Source</span>
            </div>
            {/* Rows */}
            {display.map(inv => {
              const od = isOverdue(inv)
              const dim = inv.status === 'paid' || inv.status === 'rejected'
              return (
                <div key={inv.id} onClick={() => setDetail(inv)}
                  className={`flex items-center gap-4 px-5 py-3 border-b border-edge-row-faint last:border-b-0 cursor-pointer hover:bg-surface-row-hover transition-colors ${dim ? 'opacity-[0.62]' : ''}`}
                  style={{ borderLeft: `2px solid ${od ? attention.indicator : 'transparent'}`, ...(od ? { background: attention['row-tint'] } : {}) }}>
                  <span className="w-32 flex-shrink-0 text-12 font-medium text-ink-secondary tabular-nums truncate">{inv.invoiceNumber || '—'}</span>
                  <span className="flex-1 min-w-0 text-14 font-medium text-ink-primary truncate">{inv.vendorName}</span>
                  <span className="w-24 flex-shrink-0 text-right text-14 font-medium text-ink-primary tabular-nums">{money(inv.amount ?? 0, inv.currency)}</span>
                  <span className="w-[132px] flex-shrink-0 text-right">
                    <span className={`block text-12 font-medium tabular-nums ${od ? '' : 'text-ink-secondary'}`} style={od ? { color: attention.wordmark } : undefined}>{fmtDate(inv.dueDate)}</span>
                    {od && <span className="block text-11 font-semibold uppercase tracking-overline" style={{ color: attention.wordmark }}>Overdue</span>}
                  </span>
                  <span className="w-[120px] flex-shrink-0"><InvoiceStatus status={inv.status} /></span>
                  <span className="w-12 flex-shrink-0 text-12 text-ink-muted truncate">{inv.source || '—'}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {detail && (
        <InvoiceDrawer
          invoice={detail}
          onClose={() => setDetail(null)}
          onApprove={(id) => decide(id, 'approve')}
          onReject={(id) => decide(id, 'reject')}
          busy={busy}
        />
      )}
    </div>
  )
}
